// ─────────────────────────────────────────────────────────────────────────────
// appConstants.ts — Admin Console (data-platform-console)
// All user-facing strings live in UI_TEXT. Components must not hardcode text.
// ─────────────────────────────────────────────────────────────────────────────

const CHART_TYPE = {
    BAR:           "bar",
    PIE:           "pie",
    STACKBAR:      "stackedbar",
    LINE:          "line",
    HORIZONTALBAR: "horizontalBar",
    DONUT:         "donut",
    SCATTER:       "scatter",
    BUBBLE:        "bubble",
    TABLE:         "table",
    TABLEBAR:      "tableBar",
    TABVIEW:       "tabView",
    METRIC:        "metric",
    HISTOGRAM:     "histogram",
    HEATMAP:       "heatmap",
    MULTILINE:        "multiline",
    MULTIBAR:         "multibar",
    FUNNEL:           "funnel",
    CONVERSIONFUNNEL: "conversionFunnel",
    STAGEFUNNEL:      "stageFunnel",
    MAP:              "map",
};


const CHART_TYPE_BY_LOWER: Record<string, string> = Object.values(CHART_TYPE).reduce(
    (acc, value) => ({ ...acc, [value.toLowerCase()]: value }),
    {} as Record<string, string>
);

const normalizeChartType = (type: string): string =>
    CHART_TYPE_BY_LOWER[(type || "").trim().toLowerCase()] || type;

// Chart types with a real ChartRenderer implementation. Anything else falls
// back to the decorative ChartTypePreview. Single source of truth shared by
// ConfigLayoutVisualizations.tsx and Layout.tsx so both stay in sync when a
// chart type gains real rendering support.
const RENDERABLE_CHART_TYPES: string[] = [
    CHART_TYPE.METRIC, CHART_TYPE.TABVIEW, CHART_TYPE.HORIZONTALBAR, CHART_TYPE.BAR,
    CHART_TYPE.PIE, CHART_TYPE.LINE, CHART_TYPE.SCATTER, CHART_TYPE.BUBBLE,
    CHART_TYPE.TABLE, CHART_TYPE.TABLEBAR, CHART_TYPE.MULTIBAR, CHART_TYPE.STACKBAR,
    CHART_TYPE.DONUT, CHART_TYPE.HISTOGRAM, CHART_TYPE.MULTILINE, CHART_TYPE.FUNNEL,
    CHART_TYPE.CONVERSIONFUNNEL, CHART_TYPE.STAGEFUNNEL, CHART_TYPE.HEATMAP,
];

const ENTITY_TYPE = {
    DASHBOARD:     "dashboard",
    VISUALIZATION: "visualization",
    USER:          "user",
    ROLE:          "role",
};

const MOCK_CHART_DATA = [
    {
        id: 0,
        vizType: "bar",
        headerName: "Show Legend",
        color: "",
        plots: [
            { id: 0, value: "Jan" },
            { id: 1, value: "Feb" },
            { id: 2, value: "Mar" },
        ],
    },
    {
        id: 1,
        vizType: "bar",
        headerName: "Region 1",
        color: "#00C6A4",
        plots: [
            { id: 0, value: 175 },
            { id: 1, value: 100 },
            { id: 2, value: 120 },
        ],
    },
];

export const UI_TEXT = {

    // ── Visualization (admin JSON-authoring reference docs — not translated;
    // technical field documentation, same category as QUERY_FIELD_DOCS below.
    // General VISUALIZATION UI chrome now lives in i18n/locales/{en,hi}.json.) ─
    VISUALIZATION: {
        // ── Field reference panel headers ─────────────────────────────────────
        QUERY_REF_TITLE:  "Query field reference",
        QUERY_REF_SUB:    "Hover over a field name for help, or click to keep it open",
        CONFIG_REF_TITLE: "Config field reference",
        CONFIG_REF_SUB:   "Hover over a field name for help, or click to keep it open",

        // ── Starter template info note ────────────────────────────────────────
        TEMPLATE_NOTE_PREFIX: "New visualization — starter template loaded",
        TEMPLATE_NOTE_CHART:  "starter template loaded for chart type:",
        TEMPLATE_NOTE_QUERY:  "Edit the indexName, dateRefField, profession, and aggregation fields to match your data before saving.",
        TEMPLATE_NOTE_CONFIG: "Replace YOUR_AGGREGATION_NAME with the same aggregation name you used in the Data tab's aggrQuery before saving.",

        // ── Config error banner ───────────────────────────────────────────────
        CONFIG_ERROR_TITLE: "Additional Config is required and cannot be empty.",
        CONFIG_ERROR_BODY:  "Replace YOUR_AGGREGATION_NAME with your actual aggregation name from the Data tab before saving.",

        // ── Field tooltips — Query (JSON) ─────────────────────────────────────
        QUERY_TOOLTIP_TITLE:   "Query",
        QUERY_TOOLTIP_DESC:    "A JSON array that tells the system what data to fetch from Elasticsearch. It contains the index name, filters, and aggregation logic.",
        QUERY_TOOLTIP_EXAMPLE: '[{"module": "COMMON", "dataSource": "ES", "indexName": "...", "aggrQuery": "..."}]',
        QUERY_TOOLTIP_NOTE:    "Must be valid JSON. The system will highlight errors if the format is incorrect.",

        // ── Field tooltips — Additional Config (JSON) ─────────────────────────
        CONFIG_TOOLTIP_TITLE:   "Additional config",
        CONFIG_TOOLTIP_DESC:    "Controls how the chart renders and calculates its value. Replace YOUR_AGGREGATION_NAME with the same name used in aggrQuery on the Data tab.",
        CONFIG_TOOLTIP_EXAMPLE: '{"chartType": "metric", "aggregationPaths": ["myCount"], "resultType": "value", ...}',
        CONFIG_TOOLTIP_NOTE:    "aggregationPaths must exactly match the aggregation name in your aggrQuery.",

        // ── Config textarea description ───────────────────────────────────────
        CONFIG_TEXTAREA_DESC: "Controls how this visualization renders and calculates its value.",

        // ── Filters tab (KPI-level / chart-local filters) ─────────────────────
        FIELD_KPI_FILTERS:        "KPI filters",
        KPI_FILTERS_REF_TITLE:    "KPI filter field reference",
        KPI_FILTERS_REF_SUB:      "Hover over a field name for help, or click to keep it open",
        KPI_FILTERS_HINT:         "These filters apply only to this chart, on top of any dashboard-level global filters.",
        KPI_FILTERS_TEMPLATE_NOTE: "✏️ New visualization — the JSON below is a starter template. Edit it before saving.",

        // ── Field tooltips — KPI filters ──────────────────────────────────────
        KPI_FILTERS_TOOLTIP_TITLE:   "KPI filters",
        KPI_FILTERS_TOOLTIP_DESC:    "A JSON array that defines filter widgets shown locally on this chart only, in addition to the dashboard's global filters. Each entry needs a key (the ES field, already declared in this chart's requestQueryMap), a label (shown to users), a type, and options where applicable.",
        KPI_FILTERS_TOOLTIP_EXAMPLE: '[{"key": "orgType", "label": "Facility Type", "type": "select", "options": ["Govt","Private"]}]',
        KPI_FILTERS_TOOLTIP_NOTE:    "Optional — leave as an empty array [] if this chart has no local filters. Must be valid JSON.",

        // ── Field tooltips — Info tab ─────────────────────────────────────────
        TITLE_TOOLTIP_TITLE:    "Visualization title",
        TITLE_TOOLTIP_DESC:     "This is the name that will appear at the top of the chart when it is displayed on the dashboard. Keep it short and descriptive.",
        TITLE_TOOLTIP_EXAMPLE:  "Total Registered Students",

        SUBTITLE_TOOLTIP_TITLE:   "Subtitle",
        SUBTITLE_TOOLTIP_DESC:    "A short supporting line that appears below the title. Use this to add context — for example the time period or data source the chart represents.",
        SUBTITLE_TOOLTIP_EXAMPLE: "As of March 2026 · Bihar",

        VIZ_CODE_TOOLTIP_TITLE:   "Visualization code",
        VIZ_CODE_TOOLTIP_DESC:    "A unique internal identifier for this chart. Used by the system to link this visualization to dashboards. Once set, do not change it.",
        VIZ_CODE_TOOLTIP_EXAMPLE: "aastrikaTotalUserReg",
        VIZ_CODE_TOOLTIP_NOTE:    "Use camelCase with no spaces. Changing this after creation will break existing dashboard links.",

        CHART_TYPE_TOOLTIP_TITLE:   "Chart type",
        CHART_TYPE_TOOLTIP_DESC:    "Choose how the data should be displayed visually. Use Metric for a single number, Bar/Horizontal Bar for comparisons, Line for trends over time, Pie for proportions, and Table for detailed lists.",
        CHART_TYPE_TOOLTIP_EXAMPLE: "Metric — for a single KPI like total count. Bar — for comparing districts or blocks.",
    },

    // ── Dashboard form starter templates ───────────────────────────────────────
    // Pre-populated in the Filters, Config and Widgets tabs when a new dashboard
    // is created. Also used to detect unedited templates before save.
    DASHBOARD_TEMPLATES: {
        VISUAL_CONFIG: JSON.stringify(
            [
                {
                    key: "District",
                    values: ["Araria", "Arwal", "Aurangabad", "Banka"],
                },
                {
                    key: "Block",
                    groups: [
                        {
                            name: "Patna",
                            items: ["Patna Sadar", "Phulwari", "Danapur"],
                        },
                    ],
                },
            ],
            null,
            2
        ),
        DASHBOARD_CONFIG: JSON.stringify(
            {
                title: "Dashboard Title",
                filter: "",
                isDecimal: false,
                chartTitle: "Chart Title",
            },
            null,
            2
        ),
        WIDGETS_CONFIG: JSON.stringify(
            [
                {
                    id: "totalRegisteredUsers",
                    name: "Total Registered Users",
                    code: "",
                    chartType: "metric",
                    order: 1,
                    filter: "",
                    headers: [],
                },
            ],
            null,
            2
        ),
    },
     VISUALIZATION_TEMPLATES: {
        KPI_FILTERS_CONFIG: JSON.stringify([], null, 2),
    },

   
    QUERY_FIELD_DOCS: {
        MODULE: {
            LABEL:       "module",
            DESCRIPTION: 'Identifies which part of the platform this query belongs to. Always "COMMON".',
            EXAMPLE:     '"module": "COMMON"',
        },
        DATA_SOURCE: {
            LABEL:       "dataSource",
            DESCRIPTION: '"ES" means Elasticsearch — the platform\'s main analytics database. Always "ES".',
            EXAMPLE:     '"dataSource": "ES"',
        },
        ES_INSTANCE: {
            LABEL:       "esInstance",
            DESCRIPTION: "Specifies which Elasticsearch cluster to query. QUADNARY is the production cluster.",
            EXAMPLE:     '"esInstance": "QUADNARY"',
            NOTE:        "Contact the technical team if you are unsure which instance to use.",
        },
        INDEX_NAME: {
            LABEL:       "indexName",
            DESCRIPTION: "The Elasticsearch index to query. Each index contains a different type of data.",
            EXAMPLE:     '"indexName": "dev-aastrika-user-registration-data"',
            NOTE:        "Use the exact index name. A wrong index name will return no data.",
        },
        DATE_REF_FIELD: {
            LABEL:       "dateRefField",
            DESCRIPTION: "The date field used when a date range filter is applied on the dashboard.",
            EXAMPLE:     '"dateRefField": "registrationDateTimestamp"',
        },
        REQUEST_QUERY_MAP: {
            LABEL:       "requestQueryMap",
            DESCRIPTION: 'Maps dashboard filter labels (District, Block) to actual database field names.',
            EXAMPLE:     '"requestQueryMap": "{\\"District\\": \\"district\\"}"',
        },
        AGGR_QUERY: {
            LABEL:       "aggrQuery",
            DESCRIPTION: "The core Elasticsearch aggregation query defining what data to fetch.",
            EXAMPLE:     '{"size": 0, "aggs": {"myCount": {"filter": {...}}}}',
            NOTE:        "Contact the technical team for new query patterns.",
        },
    },

    // ── Config field reference docs (Additional Config tab) ───────────────────
    CONFIG_FIELD_DOCS: {
        CHART_TYPE: {
            LABEL:       "chartType",
            DESCRIPTION: "Must match the chart type selected in the Info tab.",
            EXAMPLE:     '"chartType": "metric"',
            VALUES:      "metric, bar, horizontalBar, pie, line, table, scatter, bubble",
        },
        AGGREGATION_PATHS: {
            LABEL:       "aggregationPaths",
            DESCRIPTION: "Must exactly match the aggregation name used in aggrQuery on the Data tab.",
            EXAMPLE:     '"aggregationPaths": ["aastrikaTotalUserReg"]',
            NOTE:        "A mismatch will result in the chart showing no data.",
        },
        RESULT_TYPE: {
            LABEL:       "resultType",
            DESCRIPTION: "Tells the system how to read the value returned by the aggregation.",
            EXAMPLE:     '"resultType": "value"',
            VALUES:      "value — reads a single number. object — reads from a nested object.",
        },
        ACTION: {
            LABEL:       "action",
            DESCRIPTION: "Mathematical operation on the query result. Leave empty for most chart types.",
            EXAMPLE:     '"action": "COUNT_OF_RECORDS"',
            VALUES:      "COUNT_OF_RECORDS, COUNT_OF_RECORDS_NON_ZERO, SUM_OF_VALUES, LIST_OF_ALL_VALUES",
        },
        DOCUMENT_TYPE: {
            LABEL:       "documentType",
            DESCRIPTION: 'Always "_doc" for all current Elasticsearch 7.x indices.',
            EXAMPLE:     '"documentType": "_doc"',
            NOTE:        'Always use "_doc".',
        },
        DRILL_CHART: {
            LABEL:       "drillChart",
            DESCRIPTION: 'Set to a visualization code to enable drill-down. "none" disables it.',
            EXAMPLE:     '"drillChart": "none"',
            NOTE:        "Set to the visualization code of the target chart to enable drill-down.",
        },
        VALUE_TYPE: {
            LABEL:       "valueType",
            DESCRIPTION: "Defines how the value is formatted when displayed.",
            EXAMPLE:     '"valueType": "number"',
            VALUES:      "number, percentage",
        },
        IS_DECIMAL: {
            LABEL:       "isDecimal",
            DESCRIPTION: 'false for whole numbers, true for fractional values.',
            EXAMPLE:     '"isDecimal": false',
        },
        FILTER: {
            LABEL:       "filter",
            DESCRIPTION: "Additional filter at chart level. Leave blank if not needed.",
            EXAMPLE:     '"filter": ""',
        },
        COMMENT: {
            LABEL:       "_comment",
            DESCRIPTION: "Documentation note. Not displayed to end users.",
            EXAMPLE:     '"_comment": "Total Number of User Registrations"',
        },
    },

} as const;

const MOCK_TABVIEW_DATA = [
    { tabTitle: "Bar View", chartConfig: MOCK_CHART_DATA },
    { tabTitle: "Line View", chartConfig: MOCK_CHART_DATA.map((row) => ({ ...row, vizType: "line" })) },
];

export { CHART_TYPE, ENTITY_TYPE, MOCK_CHART_DATA, MOCK_TABVIEW_DATA, normalizeChartType, RENDERABLE_CHART_TYPES };

export const isAdminConsoleUser = (user: any): boolean =>
    user?.roles?.[0]?.admin === true || user?.roles?.[0]?.superAdmin === true;

// Tabs (by tab id: 0 Info, 1 Data, 2 Filters, 3 Additional Config, 4 Access, 5 Dashboards - not by
// on-screen position, see TAB_DISPLAY_ORDER) whose RHF fields should show the red error dot when validation fails.
export const TAB_FIELD_MAP: Record<number, string[]> = {
    0: ['title', 'chartType', 'visualizationCode'],
    1: ['queries'],
    2: ['kpiFilters'],
    3: ['config'],
    4: [],
    5: [],
};

export const TAB_NAMES = ['Info', 'Data', 'Filters', 'Additional Config', 'Access', 'Dashboards'];

// The order the visualization editor's tabs appear in, as tab ids. Dashboards comes right after Info
// because it is used constantly while building a dashboard. The ids themselves do not change.
export const TAB_DISPLAY_ORDER = [0, 5, 1, 2, 3, 4];

// Which tab a starter-template-backed field lives on, for the yellow warning dot.
export const TEMPLATE_TAB_LABEL: Record<string, string> = {
    queries: 'Data',
    config: 'Additional Config',
    kpiFilters: 'Filters',
};

export const TEMPLATE_KEYS = ['queries', 'config', 'kpiFilters'];

// ── Same purpose as the TAB_FIELD_MAP/TAB_NAMES/TEMPLATE_* group above, but
// for the Dashboards form — kept as separate exports since the two forms'
// tabs and fields are genuinely different, not aliases of one another.
export const DASHBOARD_TAB_FIELD_MAP: Record<number, string[]> = {
    0: ['title', 'description', 'dashboardId'],
    1: [],
    2: ['visualConfig'],
    3: ['dashboardConfig'],
    4: ['widgetsConfig'],
    5: [],
};

export const DASHBOARD_TAB_NAMES = ['Info', 'Layout', 'Filters', 'Config', 'Widgets', 'Access'];

// Mirrors the backend DB column length limits for the dashboard title/description
// (confirmed via direct API testing, binary-searched to the exact boundary).
// A save past these limits returns HTTP 200 (echoing the submitted value back)
// but the backend silently fails to persist it — a subsequent GET still returns
// the previous value. Enforced client-side so that failure mode is never hit.
export const DASHBOARD_TITLE_MAX_LENGTH = 200;
export const DASHBOARD_DESCRIPTION_MAX_LENGTH = 255;

// The save API only accepts this profile. Older dashboards may be stored with
// another one (e.g. "Kronos"), so saves always send this instead of the stored
// value. Reads (getDashboard) must keep using the stored profile.
export const DASHBOARD_PROFILE = 'drishti';

export const DASHBOARD_TEMPLATE_TAB_LABEL: Record<string, string> = {
    visualConfig: 'Filters',
    dashboardConfig: 'Config',
    widgetsConfig: 'Widgets',
};

export const DASHBOARD_TEMPLATE_KEYS = ['visualConfig', 'dashboardConfig', 'widgetsConfig'];

export const DASHBOARD_STARTER_TEMPLATES: Record<string, string> = {
    visualConfig: UI_TEXT.DASHBOARD_TEMPLATES.VISUAL_CONFIG,
    dashboardConfig: UI_TEXT.DASHBOARD_TEMPLATES.DASHBOARD_CONFIG,
    widgetsConfig: UI_TEXT.DASHBOARD_TEMPLATES.WIDGETS_CONFIG,
};

